if (!customElements.get('store-locator')) {
  const MARKER_CLUSTERER_SRC =
    'https://unpkg.com/@googlemaps/markerclusterer/dist/index.min.js';

  let googleMapsLoaderPromise = null;
  let markerClustererLoaderPromise = null;

  function loadGoogleMaps(apiKey) {
    if (googleMapsLoaderPromise) return googleMapsLoaderPromise;

    googleMapsLoaderPromise = new Promise((resolve, reject) => {
      if (window.google && window.google.maps && window.google.maps.importLibrary) {
        resolve(window.google);
        return;
      }

      // Canonical Google Maps dynamic library loader bootstrap.
      // Ref: https://developers.google.com/maps/documentation/javascript/load-maps-js-api
      (function (g) {
        var h,
          a,
          k,
          p = 'The Google Maps JavaScript API',
          c = 'google',
          l = 'importLibrary',
          q = '__ib__',
          m = document,
          b = window;
        b = b[c] || (b[c] = {});
        var d = b.maps || (b.maps = {}),
          r = new Set(),
          e = new URLSearchParams(),
          u = function () {
            return (
              h ||
              (h = new Promise(function (f, n) {
                a = m.createElement('script');
                e.set('libraries', [...r].join(','));
                for (k in g)
                  e.set(
                    k.replace(/[A-Z]/g, function (t) {
                      return '_' + t[0].toLowerCase();
                    }),
                    g[k]
                  );
                e.set('callback', c + '.maps.' + q);
                a.src = 'https://maps.' + c + 'apis.com/maps/api/js?' + e;
                d[q] = f;
                a.onerror = function () {
                  return (h = n(Error(p + ' could not load.')));
                };
                a.nonce = m.querySelector('script[nonce]')?.nonce || '';
                m.head.append(a);
              }))
            );
          };
        if (d[l]) {
          console.warn(p + ' only loads once. Ignoring:', g);
        } else {
          d[l] = function (f, ...n) {
            return r.add(f) && u().then(() => d[l](f, ...n));
          };
        }
      })({ key: apiKey, v: 'weekly' });

      Promise.all([
        google.maps.importLibrary('maps'),
        google.maps.importLibrary('marker'),
        google.maps.importLibrary('geocoding')
      ])
        .then(() => resolve(window.google))
        .catch(reject);
    });

    return googleMapsLoaderPromise;
  }

  function loadMarkerClusterer() {
    if (markerClustererLoaderPromise) return markerClustererLoaderPromise;
    if (window.markerClusterer) return Promise.resolve(window.markerClusterer);

    markerClustererLoaderPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = MARKER_CLUSTERER_SRC;
      script.async = true;
      script.onload = () => resolve(window.markerClusterer);
      script.onerror = () => reject(new Error('MarkerClusterer failed to load.'));
      document.head.appendChild(script);
    });

    return markerClustererLoaderPromise;
  }

  function haversineMiles(a, b) {
    const toRad = (deg) => (deg * Math.PI) / 180;
    const R = 3958.8; // miles
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const lat1 = toRad(a.lat);
    const lat2 = toRad(b.lat);
    const h =
      Math.sin(dLat / 2) ** 2 +
      Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
    return 2 * R * Math.asin(Math.sqrt(h));
  }

  const IS_APPLE_DEVICE = (() => {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent || '';
    if (/iPhone|iPod/.test(ua)) return true;
    if (/iPad/.test(ua)) return true;
    if (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1) return true;
    if (
      /Macintosh/.test(ua) &&
      /Safari/.test(ua) &&
      !/Chrome|Chromium|Edg|Firefox/.test(ua)
    )
      return true;
    return false;
  })();

  function directionsUrl(lat, lng) {
    return IS_APPLE_DEVICE
      ? `https://maps.apple.com/?daddr=${lat},${lng}&dirflg=d`
      : `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
  }

  const MARKER_SVG_MARKUP = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 52" width="40" height="52" aria-label="Hairstory store location"><ellipse cx="20" cy="49.6" rx="5.5" ry="1.2" fill="#2B2B2B" opacity="0.15"/><path d="M20 48 L28.53 31.54 A16 16 0 1 0 11.47 31.54 Z" fill="#FF681D" stroke="#2B2B2B" stroke-opacity="0.3" stroke-width="0.75" stroke-linejoin="round"/><g fill="#FFFFFF"><rect x="15.5" y="11" width="2.8" height="14"/><rect x="21.7" y="11" width="2.8" height="14"/><rect x="18.3" y="16.9" width="3.4" height="2.2"/></g></svg>`;

  const CLUSTER_SVG_MARKUP = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" width="48" height="48" aria-label="Clustered store locations"><circle cx="24" cy="24" r="22" fill="#FF681D" fill-opacity="0.25"/><circle cx="24" cy="24" r="16.75" fill="none" stroke="#FFFFFF" stroke-width="1.5"/><circle cx="24" cy="24" r="16" fill="#FF681D"/><text x="24" y="24" fill="#FFFFFF" font-family="'Barlow Condensed','Bebas Neue','Oswald','Helvetica Neue Condensed',Impact,sans-serif" font-weight="800" font-size="18" font-stretch="condensed" letter-spacing="0.2" text-anchor="middle" dominant-baseline="central" style="font-variant-numeric: tabular-nums;">12</text></svg>`;

  let markerTemplate = null;
  function createMarkerContent() {
    if (!markerTemplate) {
      const doc = new DOMParser().parseFromString(MARKER_SVG_MARKUP, 'image/svg+xml');
      markerTemplate = doc.documentElement;
    }
    // AdvancedMarkerElement requires a unique DOM node per marker.
    return markerTemplate.cloneNode(true);
  }

  let clusterTemplate = null;
  function createClusterContent(count) {
    if (!clusterTemplate) {
      const doc = new DOMParser().parseFromString(CLUSTER_SVG_MARKUP, 'image/svg+xml');
      clusterTemplate = doc.documentElement;
    }
    const node = clusterTemplate.cloneNode(true);
    const text = node.querySelector('text');
    if (text) {
      text.textContent = String(count);
      // Shrink the digits when the count gets long so they stay inside the core.
      if (count >= 1000) text.setAttribute('font-size', '11');
      else if (count >= 100) text.setAttribute('font-size', '14');
    }
    return node;
  }

  function escapeHtml(value) {
    if (value == null) return '';
    return String(value).replace(/[&<>"']/g, (ch) => {
      return {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;'
      }[ch];
    });
  }

  customElements.define(
    'store-locator',
    class StoreLocator extends HTMLElement {
      constructor() {
        super();
        this.locations = [];
        this.markers = [];
        this.markersByLocationId = new Map();
        this.activeInfoWindow = null;
        this.activeType = 'all';
        this.searchOrigin = null;
        // The list shows this many entries at a time ("Show more" reveals the
        // next batch); the map always shows every marker.
        this.visibleCount = StoreLocator.LIST_PAGE_SIZE;
      }

      static LIST_PAGE_SIZE = 15;

      // When a search returns nothing inside the radius, show this many of
      // the nearest stores instead of an empty list.
      static NEAREST_FALLBACK_COUNT = 5;

      get searchRadiusMiles() {
        return Number(this.dataset.searchRadius) || 50;
      }

      // Types whose Hairstory inventory is guaranteed — every other type gets
      // the availability note (Ulta stocks Hairstory chainwide; salons and
      // independents aren't inventory-tracked).
      static INVENTORY_CONFIRMED_TYPES = ['Ulta'];

      // Display names for store_type values. The metaobject entries keep
      // their original values ("Independent"); only the UI label changes.
      static TYPE_LABELS = { Independent: 'Pro Affiliate' };

      availabilityNoteFor(loc) {
        const note = (this.dataset.availabilityNote || '').trim();
        if (!note) return '';
        const type = (loc.type || '').trim();
        if (StoreLocator.INVENTORY_CONFIRMED_TYPES.includes(type)) return '';
        return note;
      }

      async connectedCallback() {
        this.dataScript = this.querySelector('[data-store-locator-data]');
        this.listEl = this.querySelector('[data-store-list]');
        this.statusEl = this.querySelector('[data-store-status]');
        this.errorEl = this.querySelector('[data-store-error]');
        this.mapEl = this.querySelector('[data-store-map]');
        this.filtersEl = this.querySelector('[data-store-filters]');
        this.searchFormEl = this.querySelector('[data-store-search-form]');
        this.searchInputEl = this.querySelector('[data-store-search]');
        this.geolocateBtn = this.querySelector('[data-store-geolocate]');

        try {
          this.locations = this.parseLocations();
        } catch (err) {
          this.showError('Unable to load store locations.');
          console.error('[store-locator] Failed to parse data:', err);
          return;
        }

        this.renderFilters();

        // The section renders all entries on page 1 via a large paginate size
        // (see the pagination note in the section file). Liquid ignores ?page=
        // for metaobjects, so overflow entries would be unreachable — surface
        // that loudly if the entry count ever outgrows the page size.
        if ((Number(this.dataset.totalPages) || 1) > 1) {
          console.warn(
            '[store-locator] Metaobject entries exceed one paginate page — ' +
              'entries beyond page 1 are NOT shown. Raise the paginate size ' +
              'in sections/store-locator.liquid or move to another data source.'
          );
        }

        const apiKey = this.dataset.apiKey;
        if (!apiKey) {
          this.showError(
            'Map cannot load: missing Google Maps API key. Set it in the theme editor.'
          );
          this.applyView();
          return;
        }

        try {
          await loadGoogleMaps(apiKey);
          await loadMarkerClusterer();
        } catch (err) {
          this.showError('Could not load the map. Please try again later.');
          console.error('[store-locator] Map load failed:', err);
          this.applyView();
          return;
        }

        this.initMap();
        this.bindSearch();
        this.bindGeolocate();
        this.applyView();
      }

      parseLocations() {
        if (!this.dataScript) return [];
        const raw = this.dataScript.textContent.trim();
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return parsed
          .filter((loc) => Number.isFinite(loc.lat) && Number.isFinite(loc.lng))
          .map((loc) => ({
            ...loc,
            lat: Number(loc.lat),
            lng: Number(loc.lng)
          }));
      }

      initMap() {
        const center = {
          lat: Number(this.dataset.defaultLat) || 39.8283,
          lng: Number(this.dataset.defaultLng) || -98.5795
        };
        const zoom = Number(this.dataset.defaultZoom) || 4;
        const mapOptions = {
          center,
          zoom,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false
        };
        if (this.dataset.mapId) mapOptions.mapId = this.dataset.mapId;
        this.map = new google.maps.Map(this.mapEl, mapOptions);
        // headerDisabled removes Google's default close-button row so it doesn't
        // push our content down — we render our own absolute-positioned X.
        this.infoWindow = new google.maps.InfoWindow({ headerDisabled: true });
      }

      // --- Filtering -------------------------------------------------------

      getTypes() {
        // Distinct non-empty type values. Known types get a fixed pill order
        // (Ulta first); anything new lands after, in first-seen order.
        const TYPE_ORDER = ['Ulta', 'Salon', 'Independent'];
        const seen = [];
        this.locations.forEach((loc) => {
          const t = (loc.type || '').trim();
          if (t && !seen.includes(t)) seen.push(t);
        });
        const rank = (t) => {
          const i = TYPE_ORDER.indexOf(t);
          return i === -1 ? TYPE_ORDER.length : i;
        };
        return seen.sort((a, b) => rank(a) - rank(b));
      }

      renderFilters() {
        if (!this.filtersEl) return;
        const types = this.getTypes();
        // No categories present → no filter UI; locator behaves as before.
        if (!types.length) {
          this.filtersEl.hidden = true;
          return;
        }
        this.filtersEl.hidden = false;
        this.filtersEl.innerHTML = '';
        ['all', ...types].forEach((opt) => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className =
            'store-locator__filter' + (opt === this.activeType ? ' is-active' : '');
          btn.dataset.filter = opt;
          btn.textContent =
            opt === 'all' ? 'All' : StoreLocator.TYPE_LABELS[opt] || opt;
          btn.setAttribute('aria-pressed', opt === this.activeType ? 'true' : 'false');
          btn.addEventListener('click', () => this.setFilter(opt));
          this.filtersEl.appendChild(btn);
        });
      }

      setFilter(type) {
        if (type === this.activeType) return;
        this.activeType = type;
        this.visibleCount = StoreLocator.LIST_PAGE_SIZE;
        if (this.filtersEl) {
          this.filtersEl
            .querySelectorAll('.store-locator__filter')
            .forEach((b) => {
              const on = b.dataset.filter === type;
              b.classList.toggle('is-active', on);
              b.setAttribute('aria-pressed', on ? 'true' : 'false');
            });
        }
        this.applyView();
      }

      getActiveLocations() {
        if (this.activeType === 'all') return this.locations.slice();
        return this.locations.filter(
          (loc) => (loc.type || '') === this.activeType
        );
      }

      // Central recompute: applies the active filter + any active search,
      // then re-renders markers (if the map exists), the list, and the status.
      // A search limits the LIST to stores within the search radius (falling
      // back to the nearest few when none are in range); the MAP keeps every
      // marker for the active filter so zooming out still shows the network.
      applyView({ refit = true } = {}) {
        const active = this.getActiveLocations();
        let listItems = active;
        let mapItems = active;
        this.searchFellBack = false;
        if (this.searchOrigin) {
          const origin = this.searchOrigin;
          const sorted = active
            .map((loc) => ({ ...loc, distance: haversineMiles(origin, loc) }))
            .sort((a, b) => a.distance - b.distance);
          mapItems = sorted;
          const within = sorted.filter(
            (loc) => loc.distance <= this.searchRadiusMiles
          );
          if (within.length) {
            listItems = within;
          } else {
            listItems = sorted.slice(0, StoreLocator.NEAREST_FALLBACK_COUNT);
            this.searchFellBack = true;
          }
        }
        if (this.map) {
          this.renderMarkers(mapItems);
          if (refit && !this.searchOrigin) this.fitToMarkers(mapItems);
        }
        this.renderList(listItems);
        this.updateStatus(this.statusText(listItems.length));
      }

      statusText(count) {
        const noun =
          this.activeType === 'all'
            ? count === 1
              ? 'store'
              : 'stores'
            : `${this.activeType} ${count === 1 ? 'location' : 'locations'}`;
        if (!this.searchOrigin) return `${count} ${noun}`;
        if (this.searchFellBack) {
          return `No ${noun} within ${this.searchRadiusMiles} miles — showing the nearest ${count}`;
        }
        return `${count} ${noun} within ${this.searchRadiusMiles} miles`;
      }

      // --- Map markers -----------------------------------------------------

      renderMarkers(items) {
        const { AdvancedMarkerElement } = google.maps.marker;

        // Tear down the previous marker set so the filter is reflected on the map.
        if (this.clusterer) this.clusterer.clearMarkers();
        this.markers.forEach((m) => (m.map = null));
        this.markersByLocationId.clear();

        this.markers = items.map((loc) => {
          const marker = new AdvancedMarkerElement({
            position: { lat: loc.lat, lng: loc.lng },
            title: loc.name,
            content: createMarkerContent()
          });
          marker.addListener('gmp-click', () => this.openInfoWindow(loc, marker));
          this.markersByLocationId.set(loc.id, marker);
          return marker;
        });

        if (window.markerClusterer) {
          if (!this.clusterer) {
            this.clusterer = new window.markerClusterer.MarkerClusterer({
              map: this.map,
              markers: this.markers,
              renderer: {
                render: ({ count, position }) =>
                  new google.maps.marker.AdvancedMarkerElement({
                    position,
                    content: createClusterContent(count),
                    title: `${count} stores`,
                    zIndex: 1000 + count
                  })
              }
            });
          } else {
            this.clusterer.addMarkers(this.markers);
          }
        } else {
          this.markers.forEach((m) => (m.map = this.map));
        }
      }

      fitToMarkers(items) {
        if (!items.length) return;
        const bounds = new google.maps.LatLngBounds();
        items.forEach((loc) => bounds.extend({ lat: loc.lat, lng: loc.lng }));
        // Padding leaves breathing room around the outermost markers; the larger
        // top value keeps the topmost marker clear of the map's UI controls.
        this.map.fitBounds(bounds, { top: 56, right: 24, bottom: 24, left: 24 });
        // fitBounds on a single point (or very close points) zooms to max;
        // cap once the map settles so we don't end up at street level.
        google.maps.event.addListenerOnce(this.map, 'idle', () => {
          if (this.map.getZoom() > 12) this.map.setZoom(12);
        });
      }

      renderList(items) {
        this.listEl.innerHTML = '';
        if (!items.length) {
          const li = document.createElement('li');
          li.className = 'store-locator__empty';
          li.textContent = 'No stores found.';
          this.listEl.appendChild(li);
          return;
        }
        const frag = document.createDocumentFragment();
        items.slice(0, this.visibleCount).forEach((loc) => {
          const li = document.createElement('li');
          li.className = 'store-locator__item';
          li.dataset.locationId = loc.id;
          li.innerHTML = this.buildListItemHtml(loc);
          li.addEventListener('click', () => this.focusLocation(loc));
          frag.appendChild(li);
        });
        if (items.length > this.visibleCount) {
          const li = document.createElement('li');
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'store-locator__show-more';
          btn.textContent = `Show more (${items.length - this.visibleCount} remaining)`;
          btn.addEventListener('click', () => {
            this.visibleCount += StoreLocator.LIST_PAGE_SIZE;
            this.renderList(items);
          });
          li.appendChild(btn);
          frag.appendChild(li);
        }
        this.listEl.appendChild(frag);
      }

      buildListItemHtml(loc) {
        const distance =
          typeof loc.distance === 'number'
            ? `<div class="store-locator__item-distance">${loc.distance.toFixed(1)} mi</div>`
            : '';
        const phone = loc.phone
          ? `<div class="store-locator__item-meta"><a href="tel:${escapeHtml(loc.phone)}">${escapeHtml(loc.phone)}</a></div>`
          : '';
        const address = loc.address
          ? `<div class="store-locator__item-address">${escapeHtml(loc.address)}</div>`
          : '';
        const availabilityNote = this.availabilityNoteFor(loc);
        const note = availabilityNote
          ? `<div class="store-locator__item-note">${escapeHtml(availabilityNote)}</div>`
          : '';
        return `
          <div class="store-locator__item-name">${escapeHtml(loc.name)}</div>
          ${address}
          ${distance}
          ${phone}
          ${note}
        `;
      }

      focusLocation(loc) {
        const marker = this.markersByLocationId.get(loc.id);
        if (!marker) return;
        this.map.panTo({ lat: loc.lat, lng: loc.lng });
        if (this.map.getZoom() < 12) this.map.setZoom(12);
        this.openInfoWindow(loc, marker);

        this.listEl
          .querySelectorAll('.store-locator__item')
          .forEach((el) =>
            el.classList.toggle(
              'is-active',
              el.dataset.locationId === String(loc.id)
            )
          );

        // Mobile: the list sits below the map. Scroll the page so the map's
        // top lands just under the fixed header, not behind it. We use an
        // INSTANT scroll (no smooth) because the site has a scroll listener
        // on the sticky header that interrupts in-flight smooth scrolls,
        // leaving them short of target. Instant scroll lands at the exact
        // computed Y reliably.
        if (window.matchMedia('(max-width: 768px)').matches) {
          const headerVar = getComputedStyle(document.documentElement)
            .getPropertyValue('--header-height')
            .trim();
          const headerHeight = parseFloat(headerVar) || 145;
          const mapTopDoc =
            this.mapEl.getBoundingClientRect().top + window.scrollY;
          const targetY = Math.max(0, mapTopDoc - headerHeight - 8);
          window.scrollTo(0, targetY);
        }
      }

      openInfoWindow(loc, marker) {
        if (this.activeInfoWindow) this.activeInfoWindow.close();
        this.infoWindow.setContent(this.buildInfoWindowContent(loc));
        this.infoWindow.open({ anchor: marker, map: this.map });
        this.activeInfoWindow = this.infoWindow;
      }

      buildInfoWindowContent(loc) {
        const directionsURI = directionsUrl(loc.lat, loc.lng);
        const addressRow = loc.address
          ? `<p class="store-locator__info-window-address">${escapeHtml(loc.address)}</p>`
          : '';
        const phoneRow = loc.phone
          ? `<p class="store-locator__info-window-phone"><a href="tel:${escapeHtml(loc.phone)}">${escapeHtml(loc.phone)}</a></p>`
          : '';
        const websiteBtn = loc.website
          ? `<a class="store-locator__action store-locator__action--secondary" href="${escapeHtml(loc.website)}" target="_blank" rel="noopener">Website</a>`
          : '';
        const callBtn = loc.phone
          ? `<a class="store-locator__action store-locator__action--secondary" href="tel:${escapeHtml(loc.phone)}">Call</a>`
          : '';

        const availabilityNote = this.availabilityNoteFor(loc);
        const noteRow = availabilityNote
          ? `<p class="store-locator__info-window-note">${escapeHtml(availabilityNote)}</p>`
          : '';

        const root = document.createElement('div');
        root.className = 'store-locator__info-window';
        root.innerHTML = `
          <button type="button" class="store-locator__info-window-close" data-close aria-label="Close store info">&times;</button>
          <p class="store-locator__info-window-name">${escapeHtml(loc.name)}</p>
          ${addressRow}
          ${phoneRow}
          ${noteRow}
          <div class="store-locator__info-window-actions">
            <a class="store-locator__action store-locator__action--directions" href="${escapeHtml(directionsURI)}" target="_blank" rel="noopener">Get directions &rarr;</a>
            ${callBtn}
            ${websiteBtn}
          </div>
        `;
        const closeBtn = root.querySelector('[data-close]');
        if (closeBtn) {
          closeBtn.addEventListener('click', () => this.infoWindow.close());
        }
        return root;
      }

      bindSearch() {
        if (!this.searchFormEl) return;
        this.searchFormEl.addEventListener('submit', (e) => {
          e.preventDefault();
          const q = this.searchInputEl.value.trim();
          if (!q) return;
          this.geocodeAndCenter(q);
        });
      }

      bindGeolocate() {
        if (!this.geolocateBtn) return;
        this.geolocateBtn.addEventListener('click', () => {
          if (!navigator.geolocation) {
            this.showError(
              'Geolocation is not supported by your browser. Try entering a zip or city above.'
            );
            return;
          }
          this.updateStatus('Locating you…');
          navigator.geolocation.getCurrentPosition(
            (pos) => {
              this.hideError();
              this.centerOn({
                lat: pos.coords.latitude,
                lng: pos.coords.longitude
              });
            },
            (err) => {
              this.showError(this.geolocationErrorMessage(err));
              this.updateStatus(this.statusText(this.getActiveLocations().length));
            },
            // High accuracy where available; a 10s ceiling; accept a cached fix
            // up to 10 min old to avoid a needless lookup when one is on hand.
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 600000 }
          );
        });
      }

      geolocationErrorMessage(err) {
        switch (err && err.code) {
          case 1: // PERMISSION_DENIED
            return 'Location access was blocked. Allow location for this site in your browser, or enter a zip or city above.';
          case 2: // POSITION_UNAVAILABLE
            return "We couldn't determine your location — your device's location services may be turned off. Enter a zip or city above instead.";
          case 3: // TIMEOUT
            return 'Locating you took too long. Please try again, or enter a zip or city above.';
          default:
            return 'Could not get your location. Try entering a zip or city above.';
        }
      }

      geocodeAndCenter(query) {
        const geocoder = new google.maps.Geocoder();
        this.updateStatus('Searching…');
        geocoder
          .geocode({ address: query })
          .then(({ results }) => {
            if (!results || !results.length) {
              this.showError(`No location found for "${query}".`);
              this.updateStatus(this.statusText(this.getActiveLocations().length));
              return;
            }
            this.hideError();
            const loc = results[0].geometry.location;
            this.centerOn({ lat: loc.lat(), lng: loc.lng() });
          })
          .catch((err) => {
            console.error('[store-locator] Geocode failed:', err);
            this.showError('Search failed. Please try again.');
            this.updateStatus(this.statusText(this.getActiveLocations().length));
          });
      }

      centerOn(origin) {
        this.searchOrigin = origin;
        this.visibleCount = StoreLocator.LIST_PAGE_SIZE;
        this.map.panTo(origin);
        this.map.setZoom(10);
        // Apply the active filter + distance sort; keep the search view (no refit).
        this.applyView({ refit: false });
      }

      updateStatus(text) {
        if (this.statusEl) this.statusEl.textContent = text;
      }

      showError(msg) {
        if (!this.errorEl) return;
        this.errorEl.textContent = msg;
        this.errorEl.hidden = false;
      }

      hideError() {
        if (this.errorEl) this.errorEl.hidden = true;
      }
    }
  );
}
